import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Pencil } from 'lucide-react';
import Swal from 'sweetalert2';
import { get, post, patch } from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Modal from '../../components/common/Modal.jsx';
import EstadoCuentaSocioButton from './EstadoCuentaSocioButton.jsx';

const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Number(n || 0));

/**
 * Socios -- réplica de `ListadoSocios.jsx` + `SociosCreate.jsx` (V1): tabla Nombre,
 * Participacion (con %), Por Retirar, Retirado Real, Saldo y Editar (editar + estado de
 * cuenta), fila TOTAL; SIN buscador, paginación, orden por columna ni baja. El alta
 * (Socio + Participacion) es una tarjeta lateral y el modal de edición solo cambia
 * Nombre y Participacion.
 */
export default function SociosPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso('socios:ver');
  const puedeCrear = hasPermiso('socios:crear');
  const puedeEditar = hasPermiso('socios:editar');

  const [socios, setSocios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [name, setName] = useState('');
  const [participacion, setParticipacion] = useState(0);
  const [guardando, setGuardando] = useState(false);

  const [editando, setEditando] = useState(null);
  const [editName, setEditName] = useState('');
  const [editParticipacion, setEditParticipacion] = useState(0);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const data = await get('/socios');
      setSocios([...(data || [])].sort((a, b) => (a.name > b.name ? 1 : -1)));
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function crear(e) {
    e.preventDefault();
    setGuardando(true);
    try {
      await post('/socios', { name, participacion: Number(participacion), porRetirar: 0, realRetirado: 0 });
      Swal.fire('Good job!', 'Creado con exito', 'success');
      setName(''); setParticipacion(0);
      await cargar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: err.message });
    } finally {
      setGuardando(false);
    }
  }

  async function guardarEdicion(e) {
    e.preventDefault();
    try {
      await patch(`/socios/${editando._id}`, { name: editName, participacion: Number(editParticipacion) });
      setEditando(null);
      Swal.fire('Good job!', 'Actualizado con exito', 'success');
      await cargar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: err.message });
    }
  }

  const columnas = [
    { key: 'name', label: 'Nombre' },
    { key: 'participacion', label: 'Participacion', render: (r) => `${r.participacion}%` },
    { key: 'porRetirar', label: 'Por Retirar', render: (r) => money(r.porRetirar) },
    { key: 'realRetirado', label: 'Retirado Real', render: (r) => money(r.realRetirado) },
    { key: 'saldo', label: 'Saldo', render: (r) => money((r.porRetirar || 0) - (r.realRetirado || 0)) },
    {
      key: '__acciones', label: 'Editar',
      render: (row) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {puedeEditar && (
            <button
              title="Editar" aria-label="Editar"
              className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50"
              onClick={() => { setEditando(row); setEditName(row.name); setEditParticipacion(row.participacion); }}
            >
              <Pencil size={15} />
            </button>
          )}
          <EstadoCuentaSocioButton row={row} hasPermiso={hasPermiso} />
        </div>
      ),
    },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  const totalPorRetirar = socios.reduce((a, r) => a + (r.porRetirar || 0), 0);
  const totalRealRetirado = socios.reduce((a, r) => a + (r.realRetirado || 0), 0);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-slate-900 text-center">Socios</h1>
      {error && <span className="text-sm text-danger-600">{error}</span>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <DataTable columns={columnas} data={socios} loading={cargando}
            footer={{ participacion: 'TOTAL', porRetirar: money(totalPorRetirar), realRetirado: money(totalRealRetirado) }} />
        </div>

        {puedeCrear && (
          <form onSubmit={crear} className="card p-4 space-y-3 self-start">
            <h2 className="text-center font-semibold text-slate-800">Crea una nueva Socio</h2>
            <div>
              <label className="label">Socio</label>
              <input type="text" required value={name} onChange={(e) => setName(e.target.value)} className="input-base" />
            </div>
            <div>
              <label className="label">Participacion</label>
              <input type="number" required value={participacion} onChange={(e) => setParticipacion(e.target.value)} className="input-base" />
            </div>
            <button type="submit" disabled={guardando} className="btn-primary">Guardar</button>
          </form>
        )}
      </div>

      <Modal open={!!editando} onClose={() => setEditando(null)} title="Editar Socio" width="max-w-sm"
        footer={<button type="submit" form="socio-edit-form" className="btn-primary">Guardar</button>}
      >
        <form id="socio-edit-form" onSubmit={guardarEdicion} className="space-y-3">
          <div>
            <label className="label">Nombre</label>
            <input type="text" required value={editName} onChange={(e) => setEditName(e.target.value)} className="input-base" />
          </div>
          <div>
            <label className="label">Participacion</label>
            <input type="number" required value={editParticipacion} onChange={(e) => setEditParticipacion(e.target.value)} className="input-base" />
          </div>
        </form>
      </Modal>
    </div>
  );
}
